import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import './ZZPShared.css';
import './Werkzaamheden.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const EMPTY = { label: '' };

export default function Werkzaamheden() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url  = showInactive ? '/api/zzp/werkzaamheden?all=1' : '/api/zzp/werkzaamheden';
      const res  = await authFetch(url);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ label: row.label || '' });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY);
    setError('');
  };

  const handleChange = (e) => {
    setForm((p) => ({ ...p, label: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      setError('Label is verplicht');
      return;
    }
    setSaving(true);
    const payload = { label: form.label.trim() };
    try {
      const url    = editing ? `/api/zzp/werkzaamheden/${editing.id}` : '/api/zzp/werkzaamheden';
      const method = editing ? 'PUT' : 'POST';
      const res    = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Opslaan mislukt');
      }
      await load();
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    if (!confirm(`Werkzaamheden "${row.label}" deactiveren?`)) return;
    try {
      const res = await authFetch(`/api/zzp/werkzaamheden/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="zzp-page">
      <div className="zzp-header">
        <div>
          <h2>Werkzaamheden</h2>
          <p>Standaard omschrijvingen die je in contracten kunt selecteren</p>
        </div>
        <button className="zzp-primary-btn" onClick={openCreate}>
          <Plus size={18} />
          Nieuwe werkzaamheden
        </button>
      </div>

      <div className="wz-toolbar">
        <label className="wz-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Toon inactieve werkzaamheden
        </label>
      </div>

      {loading ? (
        <div className="zzp-empty">Laden...</div>
      ) : items.length === 0 ? (
        <div className="zzp-empty">Nog geen werkzaamheden. Klik op "Nieuwe werkzaamheden" om te beginnen.</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="zzp-card-list">
            {items.map((row) => (
              <div key={row.id} className={`zzp-card${row.is_active === 0 ? ' wz-card-inactive' : ''}`} onClick={() => openEdit(row)}>
                <div className="zzp-card-top">
                  <span className="zzp-card-name wz-card-label">{row.label}</span>
                  <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                      <Pencil size={14} />
                    </button>
                    {row.is_active !== 0 && (
                      <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {row.is_active === 0 && <div className="wz-inactive-badge">Inactief</div>}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="zzp-table-wrap">
            <table className="zzp-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Actief</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} onClick={() => openEdit(row)} className={`zzp-row${row.is_active === 0 ? ' wz-row-inactive' : ''}`}>
                    <td className="zzp-cell-strong wz-label-cell">{row.label}</td>
                    <td>{row.is_active === 0 ? '—' : 'Ja'}</td>
                    <td>
                      <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                          <Pencil size={14} />
                        </button>
                        {row.is_active !== 0 && (
                          <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalOpen && (
        <div className="zzp-modal-overlay" onClick={closeModal}>
          <div className="zzp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="zzp-modal-header">
              <h3>{editing ? 'Werkzaamheden bewerken' : 'Nieuwe werkzaamheden'}</h3>
              <button className="zzp-modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="zzp-form">
              <div className="zzp-form-group">
                <label htmlFor="label">Label *</label>
                <textarea
                  id="label"
                  name="label"
                  rows={4}
                  placeholder="Omschrijving van de werkzaamheden..."
                  value={form.label}
                  onChange={handleChange}
                  autoFocus
                />
              </div>

              {error && <div className="zzp-form-error">{error}</div>}

              <div className="zzp-form-actions">
                <button type="button" className="zzp-btn-cancel" onClick={closeModal}>
                  Annuleren
                </button>
                <button type="submit" className="zzp-btn-submit" disabled={saving}>
                  {saving ? 'Opslaan...' : (editing ? 'Opslaan' : 'Toevoegen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
